import { useMemo } from "react";
import { Box, Button, Flex, Icon } from "@chakra-ui/react";
import { TbBell } from "react-icons/tb";
import { useRetryNotification } from "features/Resources/Checkpoint/hooks";
import BaseActionStatus from "components/Tag/Status/BaseActionStatus";
import { greenStyles, errorStyles, blueStyles } from "components/Tag/Status/styles";
import CDS, { ActionTypeTag } from "design-system";
import { DELIVERY_METHOD_LABELS } from "features/Resources/Shared/constants";
import { dateInFormat } from "helpers/time";

interface CheckpointNotification {
  id: number;
  notificationType: string;
  deliveryMethod: string;
  status: string;
  messageId?: string;
  channelId?: string;
  errorMessage?: string;
  createdAt: string;
  userId?: number;
  messageServiceChannelId?: number;
}

interface TaskReminderNotification {
  id: number;
  type: "task_reminder";
  taskId: number;
  taskName: string;
  recipientName: string;
  senderName?: string;
  messageId?: string;
  channelId?: string;
  createdAt: string;
}

interface Recipient {
  name: string;
  targetType: string;
  targetId: number;
  target: any;
}

interface GroupedReminder {
  messageId: string;
  recipientName: string;
  senderName?: string;
  taskCount: number;
  createdAt: string;
}

type CommunicationItem =
  | { kind: "notification"; notification: CheckpointNotification; createdAt: string }
  | { kind: "reminder"; reminder: GroupedReminder; createdAt: string };

interface CheckpointCommunicationsSectionProps {
  data: {
    checkpointNotifications?: CheckpointNotification[];
    taskReminderNotifications?: TaskReminderNotification[];
    recipients?: Recipient[];
    timezone?: string;
  };
  checkpointId?: string;
}

export default function CheckpointCommunicationsSection({ data, checkpointId }: CheckpointCommunicationsSectionProps) {
  const { mutate: retryNotification, isPending: isRetrying } = useRetryNotification(checkpointId);

  const communications: CommunicationItem[] = useMemo(() => {
    const items: CommunicationItem[] = [];
    data.checkpointNotifications?.forEach((n) =>
      items.push({ kind: "notification", notification: n, createdAt: n.createdAt }),
    );

    const remindersByMessage = new Map<string, TaskReminderNotification[]>();
    data.taskReminderNotifications?.forEach((r) => {
      const key = r.messageId || r.id.toString();
      const group = remindersByMessage.get(key) || [];
      group.push(r);
      remindersByMessage.set(key, group);
    });
    remindersByMessage.forEach((group) => {
      const first = group[0];
      items.push({
        kind: "reminder",
        createdAt: first.createdAt,
        reminder: {
          messageId: first.messageId || first.id.toString(),
          recipientName: first.recipientName,
          senderName: first.senderName,
          taskCount: group.length,
          createdAt: first.createdAt,
        },
      });
    });

    return items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [data.checkpointNotifications, data.taskReminderNotifications]);

  if (communications.length === 0) return null;

  return (
    <Box>
      <CDS.Text10
        fontSize="2xs"
        fontWeight="600"
        color="content.tertiary"
        textTransform="uppercase"
        letterSpacing="wider"
        mb="2.5"
      >
        Communications
      </CDS.Text10>
      <Flex direction="column" gap="2">
        {communications.map((item, idx) => {
          if (item.kind === "notification") {
            const n = item.notification;
            const statusStyles =
              n.status === "delivered" ? greenStyles : n.status === "partial" ? blueStyles : errorStyles;
            const statusLabel = n.status === "delivered" ? "Delivered" : n.status === "partial" ? "Partial" : "Failed";

            const recipientNames = data.recipients
              ?.map((r) => {
                if (r.targetType === "User") return r.target?.fullName || r.target?.name;
                if (r.targetType === "OnboardingJourneyNetworkRelation") return `the ${r.target?.name}`;
                if (r.targetType === "MessageServiceChannel") return `#${r.target?.name}`;
                return null;
              })
              .filter(Boolean);
            const toLine = recipientNames && recipientNames.length > 0 ? ` · ${recipientNames.join(", ")}` : "";

            return (
              <Flex key={`notif-${idx}`} align="center" gap="3" py="1">
                <ActionTypeTag.IconBadge actionType="message" size="sm" />
                <Flex direction="column" flex="1" minW="0">
                  <CDS.Text12 fontWeight="500">
                    Checkpoint notification
                    <CDS.Text10 as="span" color="content.tertiary" fontWeight="normal" ml="1">
                      · auto
                    </CDS.Text10>
                  </CDS.Text12>
                  <CDS.Text10 color="content.tertiary" noOfLines={1}>
                    {dateInFormat(n.createdAt, "MMM D [at] h:mm A", data.timezone)}
                    {" · "}
                    {DELIVERY_METHOD_LABELS[n.deliveryMethod] || n.deliveryMethod}
                    {toLine}
                  </CDS.Text10>
                </Flex>
                <Flex align="center" gap="2" flexShrink={0}>
                  <BaseActionStatus {...statusStyles}>{statusLabel}</BaseActionStatus>
                  {n.status === "failed" && checkpointId && (
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => retryNotification(n.id)}
                      isLoading={isRetrying}
                    >
                      Retry
                    </Button>
                  )}
                </Flex>
              </Flex>
            );
          }

          const r = item.reminder;
          return (
            <Flex key={`reminder-${r.messageId}`} align="center" gap="3" py="1">
              <Box
                as="span"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="28px"
                h="28px"
                p="8px"
                bg="actionType.bg"
                borderRadius="4px"
                flexShrink={0}
              >
                <Icon as={TbBell} boxSize="16px" color="actionType.icon" />
              </Box>
              <Flex direction="column" flex="1" minW="0">
                <CDS.Text12 fontWeight="500">
                  Task reminder · {r.taskCount} {r.taskCount === 1 ? "task" : "tasks"}
                </CDS.Text12>
                <CDS.Text10 color="content.tertiary" noOfLines={1}>
                  {dateInFormat(r.createdAt, "MMM D [at] h:mm A", data.timezone)}
                  {" · to "}
                  {r.recipientName}
                  {r.senderName && ` · from ${r.senderName}`}
                </CDS.Text10>
              </Flex>
              <BaseActionStatus {...greenStyles} flexShrink={0}>
                Delivered
              </BaseActionStatus>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
}
